export interface AyaSourceRef {
  title: string;
  journal: string;
  year: string;
  domain: string;
  url: string;
  snippet: string;
  kind?: string;
}

const SOURCES_BLOCK = /```(?:aya-sources|aya-source)\s*([\s\S]*?)```/i;
const PARTIAL_SOURCES = /```(?:aya-sources|aya-source)[\s\S]*$/i;
const FOLLOWUPS_BLOCK = /```(?:aya-followups|aya-followup|aya-suggestions)\s*([\s\S]*?)```/i;
const PARTIAL_FOLLOWUPS = /```(?:aya-followups|aya-followup|aya-suggestions)[\s\S]*$/i;
const URL_RE = /https?:\/\/[^\s|>\]]+/i;

function normalize(raw: unknown): AyaSourceRef | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const url =
    (typeof r.url === 'string' && r.url.trim()) ||
    (typeof r.link === 'string' && r.link.trim()) ||
    (typeof r.href === 'string' && r.href.trim()) ||
    '';
  const title =
    (typeof r.title === 'string' && r.title.trim()) ||
    (typeof r.name === 'string' && r.name.trim()) ||
    '';
  const kind = typeof r.kind === 'string' ? r.kind.trim() : '';
  const attribution =
    kind === 'model_knowledge' ||
    kind === 'literature_incomplete' ||
    (!url && Boolean(title) && Boolean(r.snippet || r.journal));
  if (!title) return null;
  if (!url && !attribution) return null;

  let domain = typeof r.domain === 'string' ? r.domain.trim() : '';
  if (!domain && url) {
    try {
      domain = new URL(url).hostname.replace(/^www\./, '');
    } catch {
      domain = '';
    }
  }
  if (!domain && kind === 'model_knowledge') domain = 'Aya knowledge';
  if (!domain && kind === 'literature_incomplete') domain = 'Literature';

  const yearRaw = r.year ?? r.publication_year ?? r.published;
  const year =
    typeof yearRaw === 'number'
      ? String(yearRaw)
      : typeof yearRaw === 'string'
        ? yearRaw.trim()
        : '';

  const snippet =
    (typeof r.snippet === 'string' && r.snippet.trim()) ||
    (typeof r.quote === 'string' && r.quote.trim()) ||
    (typeof r.excerpt === 'string' && r.excerpt.trim()) ||
    '';

  return {
    title,
    journal: typeof r.journal === 'string' ? r.journal.trim() : '',
    year,
    domain,
    url,
    snippet,
    kind: kind || undefined,
  };
}

function domainFrom(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}

function parseSourcesLines(raw: string): AyaSourceRef[] {
  const out: AyaSourceRef[] = [];
  for (const line of raw.split(/\r?\n/)) {
    const trimmed = line.replace(/^[-*•\d.)\s]+/, '').trim();
    if (!trimmed) continue;
    const urlMatch = trimmed.match(URL_RE);
    if (!urlMatch) continue;
    const url = urlMatch[0].replace(/[.,;:]+$/, '');
    const before = trimmed.slice(0, urlMatch.index).replace(/[|\-–—]\s*$/, '').trim();
    const after = trimmed
      .slice((urlMatch.index ?? 0) + urlMatch[0].length)
      .replace(/^[|\-–—\s]+/, '')
      .trim();
    const parts = before.split('|').map((p) => p.trim()).filter(Boolean);
    const title = parts[0] || domainFrom(url) || 'Source';
    const journalYear = parts[1] || '';
    const yearMatch = journalYear.match(/\b(19|20)\d{2}\b/);
    out.push({
      title,
      journal: journalYear.replace(/\b(19|20)\d{2}\b/, '').replace(/[·•]/g, ' ').trim(),
      year: yearMatch ? yearMatch[0] : '',
      domain: domainFrom(url),
      url,
      snippet: after,
    });
    if (out.length >= 6) break;
  }
  return out;
}

function parseSourcesJson(raw: string): AyaSourceRef[] {
  try {
    const parsed = JSON.parse(raw.trim());
    if (Array.isArray(parsed)) {
      return parsed.map(normalize).filter((s): s is AyaSourceRef => s !== null);
    }
    const one = normalize(parsed);
    return one ? [one] : [];
  } catch {
    return parseSourcesLines(raw);
  }
}

function parseFollowupsJson(raw: string): string[] {
  try {
    const parsed = JSON.parse(raw.trim());
    if (!Array.isArray(parsed)) return parseFollowupsLines(raw);
    return parsed
      .map((item) => {
        if (typeof item === 'string') return item.trim();
        if (item && typeof item === 'object') {
          const o = item as Record<string, unknown>;
          if (typeof o.text === 'string') return o.text.trim();
          if (typeof o.question === 'string') return o.question.trim();
          if (typeof o.prompt === 'string') return o.prompt.trim();
        }
        return '';
      })
      .filter(Boolean)
      .slice(0, 3);
  } catch {
    return parseFollowupsLines(raw);
  }
}

function parseFollowupsLines(raw: string): string[] {
  return raw
    .split(/\r?\n/)
    .map((line) => line.replace(/^[-*•\d.)\s]+/, '').trim())
    .filter(Boolean)
    .slice(0, 3);
}

export interface AyaMessageBlocks {
  body: string;
  sources: AyaSourceRef[];
  followups: string[];
}

export function splitAyaBlocks(text: string): AyaMessageBlocks {
  let body = text;
  let sources: AyaSourceRef[] = [];
  let followups: string[] = [];

  const sourcesMatch = body.match(SOURCES_BLOCK);
  if (sourcesMatch) {
    sources = parseSourcesJson(sourcesMatch[1]);
    body = body.replace(SOURCES_BLOCK, '');
  } else {
    body = body.replace(PARTIAL_SOURCES, '');
  }

  const followupsMatch = body.match(FOLLOWUPS_BLOCK);
  if (followupsMatch) {
    followups = parseFollowupsJson(followupsMatch[1]);
    body = body.replace(FOLLOWUPS_BLOCK, '');
  } else {
    body = body.replace(PARTIAL_FOLLOWUPS, '');
  }

  return { body: body.trimEnd(), sources, followups };
}

export function scrollToSourceCard(messageId: string, index: number): void {
  const el = document.getElementById(`aya-source-${messageId}-${index}`);
  if (!el) return;
  el.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  el.classList.add('aya-source-highlight');
  window.setTimeout(() => el.classList.remove('aya-source-highlight'), 1200);
}
